// Integrated comment generation and display system

import {
	ErrorSeverity,
	ErrorType,
	errorHandler,
	type MonologError,
} from "../error-handling/index.js";
import type { CommentGenerator } from "../interfaces/comment-generation";
import type { PreferenceManager } from "../preferences/preference-manager.js";
import type { IndexedDBWrapper } from "../storage/indexeddb-wrapper.js";
import {
	type Comment,
	type CommenterDefinition,
	type ConversationContext,
	DEFAULT_COMMENTER_DEFINITIONS,
	type TranscriptSegment,
	type UserInteraction,
	type UserPreferences,
} from "../types/core.js";
import { sendNotification } from "../utils/browser-notification.js";
import { HybridCommentGenerator } from "./llm-comment-generator";

/**
 * Configuration for the integrated comment system
 */
interface CommentSystemConfig {
	enableLocalLLM: boolean;
	displayConfig?: {
		maxVisibleComments: number;
		autoScroll: boolean;
		showTimestamps: boolean;
		enableInteractions: boolean;
		fadeOutDuration: number;
	};
}

/**
 * Default configuration for comment system
 */
export const DEFAULT_COMMENT_SYSTEM_CONFIG: CommentSystemConfig = {
	enableLocalLLM: true,
	displayConfig: {
		maxVisibleComments: 20,
		autoScroll: true,
		showTimestamps: false,
		enableInteractions: true,
		fadeOutDuration: 10000,
	},
};

/**
 * Integrated comment system combining generation and display
 */
export class CommentSystem implements CommentGenerator {
	private hybridGenerator: HybridCommentGenerator | null = null;
	private config: CommentSystemConfig;
	private preferenceManager: PreferenceManager | null = null;
	private currentSessionId: string | null = null;
	private storage: IndexedDBWrapper | null = null;
	private preferenceSubscription: (() => void) | null = null;
	private isCompressing = false;
	/** コメント生成中フラグ（二重生成防止） */
	private isGenerating = false;
	/** ブラウザ通知を送信するかどうか（設定値に追従） */
	private notificationsEnabled = false;

	constructor(
		config: CommentSystemConfig = DEFAULT_COMMENT_SYSTEM_CONFIG,
		preferenceManager?: PreferenceManager,
	) {
		this.config = config;
		this.preferenceManager = preferenceManager || null;

		// Initialize hybrid generator if local LLM is enabled
		if (config.enableLocalLLM) {
			this.hybridGenerator = new HybridCommentGenerator();
			console.info("LLM comment generation enabled");
		}
	}

	/**
	 * Initializes the comment system with storage access
	 */
	async initialize(
		storage: IndexedDBWrapper,
		preferenceManager?: PreferenceManager,
	): Promise<void> {
		this.storage = storage;
		if (preferenceManager) {
			this.preferenceManager = preferenceManager;
		}
		if (!this.preferenceManager) {
			console.warn(
				"PreferenceManager not provided to CommentSystem.initialize. Features requiring preferences may fail.",
			);
		}
	}

	/**
	 * Initializes the preferences with storage and applies them to generators
	 */
	async initializePreferences(
		_storage: IndexedDBWrapper,
		userId: string,
		sessionId: string,
	): Promise<void> {
		this.currentSessionId = sessionId;

		if (!this.preferenceManager) {
			console.warn(
				"PreferenceManager not initialized in CommentSystem.initializePreferences.",
			);
			return;
		}

		// Subscribe to preference changes
		if (this.preferenceSubscription) {
			this.preferenceSubscription();
		}
		this.preferenceSubscription = this.preferenceManager.subscribe((prefs) => {
			this.applyPreferencesToGenerator(prefs);
		});

		const prefs = await this.preferenceManager.initializePreferences(userId);
		this.applyPreferencesToGenerator(prefs);
	}

	// initializeDisplay removed

	/**
	 * Generates and displays multiple comments based on conversation context
	 */
	async generateComments(
		context: ConversationContext,
	): Promise<Comment[] | null> {
		// 同時実行を防ぐ（自動・手動の両方を保護）
		if (this.isGenerating) return null;
		this.isGenerating = true;
		try {
			return await this.generateCommentsInternal(context);
		} finally {
			this.isGenerating = false;
		}
	}

	/**
	 * 実際のコメント生成ロジック（generateCommentsの内部実装）
	 */
	private async generateCommentsInternal(
		context: ConversationContext,
	): Promise<Comment[] | null> {
		// 非同期処理中のセッション変更を検知するための初期値を記録
		const initialSessionId = this.currentSessionId;

		let adaptiveContext = context;

		// Fetch latest transcript from DB to ensure sync with user edits
		if (this.storage && this.currentSessionId) {
			const sessionData = await this.storage.getSession(this.currentSessionId);
			if (sessionData) {
				// 1. Get recent transcript segments and uncompressed segments via a single backward iteration.
				// This replaces multiple O(N) scans (.filter and .reduce) with a single pass
				// that stops at the first compressed segment, achieving O(U) complexity.
				const recentSegmentsText: string[] = [];
				const uncompressedSegments: TranscriptSegment[] = [];
				let totalChars = 0;
				const transcript = sessionData.transcript;

				for (let i = transcript.length - 1; i >= 0; i--) {
					const s = transcript[i];
					// Once we hit a compressed segment, we can stop because segments are chronological
					// and compression always starts from the beginning.
					if (s.isCompressed) break;

					if (s.isFinal) {
						uncompressedSegments.push(s);
						totalChars += s.text.length;
						if (recentSegmentsText.length < 5) {
							recentSegmentsText.push(s.text);
						}
					}
				}
				// Since we used push in a backward loop, reverse to restore chronological order
				uncompressedSegments.reverse();
				const recentText = recentSegmentsText.reverse().join(" ");

				// 2. Get pinned comments via single-pass loops to avoid redundant array allocations.
				const pinnedCommentIds = new Set<string>();
				for (let i = 0; i < sessionData.interactions.length; i++) {
					const interaction = sessionData.interactions[i];
					if (interaction.type === "pin") {
						pinnedCommentIds.add(interaction.commentId);
					}
				}

				const pinnedComments: string[] = [];
				for (let i = 0; i < sessionData.comments.length; i++) {
					const comment = sessionData.comments[i];
					if (pinnedCommentIds.has(comment.id)) {
						pinnedComments.push(comment.content);
					}
				}

				adaptiveContext = {
					...adaptiveContext,
					recentTranscript: recentText || adaptiveContext.recentTranscript,
					pinnedComments,
					compressedHistory: sessionData.compressedHistory,
				};

				// 3. Check for context compression
				if (
					this.hybridGenerator &&
					totalChars > 5000 &&
					uncompressedSegments.length > 5
				) {
					// Wait for compression to complete before generating comments to avoid concurrent API calls
					try {
						// Pass pre-calculated uncompressed segments to avoid O(N) filtering in triggerCompression.
						await this.triggerCompression(sessionData, uncompressedSegments);
						// Update context with potentially new summary
						adaptiveContext.compressedHistory = sessionData.compressedHistory;
					} catch (err) {
						console.error("Compression failed", err);
						await errorHandler.handleError({
							type: ErrorType.COMMENT_GENERATION,
							severity: ErrorSeverity.LOW,
							message: "履歴の要約に失敗しました。",
							originalError: err instanceof Error ? err : undefined,
							timestamp: new Date(),
						});
					}
				}
			}
		}

		let comments: Comment[] | null = null;

		// Use LLM generation
		if (this.config.enableLocalLLM && this.hybridGenerator) {
			try {
				comments = await this.hybridGenerator.generateComments(adaptiveContext);
			} catch (error) {
				console.error("Comment generation failed:", error);
				await errorHandler.handleError(
					error && typeof error === "object" && "type" in error
						? (error as unknown as MonologError)
						: {
								type: ErrorType.COMMENT_GENERATION,
								severity: ErrorSeverity.HIGH,
								message:
									error instanceof Error
										? error.message
										: "コメント生成に失敗しました。",
								originalError: error instanceof Error ? error : undefined,
								timestamp: new Date(),
							},
				);
			}
		}

		// Context Guard
		if (this.currentSessionId !== initialSessionId) {
			return null;
		}

		if (this.storage && this.currentSessionId) {
			const sessionData = await this.storage.getSession(this.currentSessionId);
			if (!sessionData || sessionData.endTime) {
				return null;
			}
		}

		// Notify listeners about generated comments
		if (comments && this.onCommentsGenerated) {
			console.log(
				"[CommentSystem Debug] onCommentsGenerated called. comments length:",
				comments.length,
			);
			this.onCommentsGenerated(comments);
			// タブがバックグラウンドにある場合など、ユーザーに生成完了を知らせる
			console.log(
				"[CommentSystem Debug] notificationsEnabled value:",
				this.notificationsEnabled,
			);
			if (this.notificationsEnabled) {
				console.log("[CommentSystem Debug] Calling sendNotification()...");
				sendNotification(
					"💬 コメントが届きました",
					`${comments.length}件のコメントが生成されました。`,
				);
			}
		}

		return comments;
	}

	/**
	 * Gets active commenters
	 */
	getActiveCommenters(): CommenterDefinition[] {
		return this.hybridGenerator
			? this.hybridGenerator.getActiveCommenters()
			: [];
	}

	public onInteraction: ((interaction: UserInteraction) => void) | null = null;
	public onCommentsGenerated: ((comments: Comment[]) => void) | null = null;
	public onCommentsClear: (() => void) | null = null;

	/**
	 * Clears all comments
	 */
	clearComments(): void {
		if (this.onCommentsClear) {
			this.onCommentsClear();
		}
	}

	/**
	 * Destroys the comment system
	 */
	async destroy(): Promise<void> {
		if (this.preferenceSubscription) {
			this.preferenceSubscription();
			this.preferenceSubscription = null;
		}

		this.hybridGenerator = null;
	}

	/**
	 * Applies current user preferences to the active generator.
	 */
	private applyPreferencesToGenerator(prefs: UserPreferences): void {
		if (!this.hybridGenerator) return;

		const commenterDefinitions = prefs.commenterDefinitions ?? [
			...DEFAULT_COMMENTER_DEFINITIONS,
		];
		this.hybridGenerator.setCommenterDefinitions(commenterDefinitions);

		if (prefs.policyConfig?.basicPolicy !== undefined) {
			this.hybridGenerator.setBasicPolicy(prefs.policyConfig.basicPolicy);
		}

		if (prefs.apiKeys?.summaryApiKey) {
			this.hybridGenerator.setApiKey(prefs.apiKeys.summaryApiKey);
		}

		if (prefs.commentMaxCount !== undefined) {
			this.hybridGenerator.setCommentMaxCount(prefs.commentMaxCount);
		}

		// 通知設定をPreferenceManagerの最新値に追従させる
		console.log(
			"[CommentSystem Debug] applyPreferencesToGenerator() - updating notificationsEnabled to:",
			prefs.notificationsEnabled,
		);
		this.notificationsEnabled = prefs.notificationsEnabled ?? false;
	}

	/**
	 * Triggers context compression
	 */
	private async triggerCompression(
		session: import("../types/core").Session,
		uncompressedFinal?: TranscriptSegment[],
	): Promise<void> {
		if (this.isCompressing || !this.hybridGenerator || !this.storage) return;

		// Performance optimization: Use pre-calculated uncompressed segments if provided.
		// If not provided, perform an optimized backward scan to find them.
		let finalSegments = uncompressedFinal;
		if (!finalSegments) {
			finalSegments = [];
			const transcript = session.transcript;
			for (let i = transcript.length - 1; i >= 0; i--) {
				const s = transcript[i];
				if (s.isCompressed) break;
				if (s.isFinal) {
					finalSegments.push(s);
				}
			}
			// Restore chronological order
			finalSegments.reverse();
		}

		if (finalSegments.length <= 5) return;

		this.isCompressing = true;
		try {
			const segmentsToCompress = finalSegments.slice(0, -5);
			const textToCompress = segmentsToCompress.map((s) => s.text).join(" ");

			const previousSummary = session.compressedHistory || "";
			const combinedText = previousSummary
				? `これまでの要約:\n${previousSummary}\n\n新しい会話:\n${textToCompress}`
				: textToCompress;

			const newSummary =
				await this.hybridGenerator.compressContext(combinedText);

			if (newSummary) {
				session.compressedHistory = newSummary;
				for (const segment of segmentsToCompress) {
					segment.isCompressed = true;
				}
				await this.storage.saveSession(session);
			}
		} catch (error) {
			console.error("Context compression failed:", error);
			await errorHandler.handleError(
				error && typeof error === "object" && "type" in error
					? (error as unknown as MonologError)
					: {
							type: ErrorType.COMMENT_GENERATION,
							severity: ErrorSeverity.LOW,
							message: "履歴の要約に失敗しました。",
							originalError: error instanceof Error ? error : undefined,
							timestamp: new Date(),
						},
			);
		} finally {
			this.isCompressing = false;
		}
	}
}
